import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X, Zap, PlayCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type Status =
  | "opportunity"
  | "ready"
  | "approaching"
  | "quote_sent"
  | "negotiation"
  | "signing"
  | "won"
  | "lost";

const STATUS_OPTIONS: { value: Status; label: string }[] = [
  { value: "opportunity", label: "Opportunity" },
  { value: "ready", label: "Ready" },
  { value: "approaching", label: "Approaching" },
  { value: "quote_sent", label: "Quote sent" },
  { value: "negotiation", label: "Negotiation" },
  { value: "signing", label: "Signing" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const STATUS_LABEL: Record<Status, string> = Object.fromEntries(
  STATUS_OPTIONS.map((s) => [s.value, s.label])
) as Record<Status, string>;

type ActionType =
  | "set_status"
  | "offset_next_action_days"
  | "add_tag"
  | "generate_brief"
  | "generate_opportunity_picture"
  | "generate_product_services_note";
type RuleAction =
  | { type: "set_status"; status: Status }
  | { type: "offset_next_action_days"; days: number }
  | { type: "add_tag"; tagName: string }
  | { type: "generate_brief" }
  | { type: "generate_opportunity_picture" }
  | { type: "generate_product_services_note" };

type TriggerType = "status_change" | "tag_added";

type DraftRule = {
  id?: string;
  name: string;
  enabled: boolean;
  triggerType: TriggerType;
  triggerValue: string;
  actions: RuleAction[];
  sortOrder: number;
};

const EMPTY_RULE: DraftRule = {
  name: "",
  enabled: true,
  triggerType: "status_change",
  triggerValue: "approaching",
  actions: [{ type: "offset_next_action_days", days: 3 }],
  sortOrder: 0,
};

function describeAction(a: RuleAction): string {
  if (a.type === "set_status") return `Set status → ${STATUS_LABEL[a.status] ?? a.status}`;
  if (a.type === "offset_next_action_days") {
    const sign = a.days > 0 ? "+" : "";
    return `Set next action → today ${sign}${a.days} day${Math.abs(a.days) === 1 ? "" : "s"}`;
  }
  if (a.type === "generate_brief") return "Generate brief (AI)";
  if (a.type === "generate_opportunity_picture") return "Generate opportunity picture (AI)";
  if (a.type === "generate_product_services_note")
    return "Generate Product & services note (AI)";
  return `Add tag "${a.tagName}"`;
}

function describeTrigger(r: { triggerType: TriggerType; triggerValue: string }): string {
  if (r.triggerType === "status_change") {
    return `Status changes to ${STATUS_LABEL[r.triggerValue as Status] ?? r.triggerValue}`;
  }
  return `Tag "${r.triggerValue}" is added`;
}

export function SettingsAutomationsPage() {
  const utils = trpc.useUtils();
  const rules = trpc.automationRules.list.useQuery();
  const tags = trpc.opportunityTags.list.useQuery();

  const tagNames = useMemo(
    () => (tags.data ?? []).map((t) => t.name).sort((a, b) => a.localeCompare(b)),
    [tags.data]
  );

  const [draft, setDraft] = useState<DraftRule | null>(null);

  const create = trpc.automationRules.create.useMutation({
    onSuccess: () => {
      utils.automationRules.list.invalidate();
      setDraft(null);
      toast.success("Rule added");
    },
    onError: (err) => toast.error(err.message || "Failed to add"),
  });

  const update = trpc.automationRules.update.useMutation({
    onSuccess: () => {
      utils.automationRules.list.invalidate();
      setDraft(null);
      toast.success("Rule updated");
    },
    onError: (err) => toast.error(err.message || "Failed to update"),
  });

  const remove = trpc.automationRules.delete.useMutation({
    onSuccess: () => {
      utils.automationRules.list.invalidate();
      toast.success("Rule removed");
    },
    onError: (err) => toast.error(err.message || "Failed to remove"),
  });

  const runScan = trpc.automationRules.runApproachScan.useMutation({
    onSuccess: (r) => toast.success(`Tagged ${r.tagged} opportunity(ies) as "Approach needed"`),
    onError: (err) => toast.error(err.message || "Scan failed"),
  });

  const items = rules.data ?? [];

  const startNew = () => setDraft({ ...EMPTY_RULE });
  const startEdit = (rule: (typeof items)[number]) =>
    setDraft({
      id: rule.id,
      name: rule.name,
      enabled: rule.enabled,
      triggerType: rule.triggerType as TriggerType,
      triggerValue: rule.triggerValue,
      actions: (rule.actions as RuleAction[]) ?? [],
      sortOrder: rule.sortOrder,
    });

  const saveDraft = () => {
    if (!draft) return;
    const name = draft.name.trim();
    if (!name) {
      toast.error("Name is required");
      return;
    }
    if (!draft.triggerValue.trim()) {
      toast.error("Trigger value is required");
      return;
    }
    if (draft.actions.length === 0) {
      toast.error("Add at least one action");
      return;
    }
    const trigger =
      draft.triggerType === "status_change"
        ? { triggerType: "status_change" as const, triggerValue: draft.triggerValue as Status }
        : { triggerType: "tag_added" as const, triggerValue: draft.triggerValue };
    const payload = {
      name,
      enabled: draft.enabled,
      actions: draft.actions,
      sortOrder: draft.sortOrder,
      ...trigger,
    };
    if (draft.id) {
      update.mutate({ id: draft.id, patch: payload });
    } else {
      create.mutate(payload);
    }
  };

  const toggleEnabled = (id: string, enabled: boolean) => {
    update.mutate({ id, patch: { enabled } });
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 md:px-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Zap className="h-6 w-6 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Automations</h1>
            <p className="text-sm text-muted-foreground">
              Rules run whenever a matching trigger fires on an opportunity. The nightly
              scan adds the <span className="font-medium">Approach needed</span> tag to
              every opportunity whose next-action date is today or earlier.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={runScan.isPending}
            onClick={() => runScan.mutate()}
          >
            <PlayCircle className="mr-2 h-4 w-4" />
            Run nightly scan now
          </Button>
          <Button size="sm" onClick={startNew} disabled={!!draft}>
            <Plus className="mr-2 h-4 w-4" />
            New rule
          </Button>
        </div>
      </div>

      {draft ? (
        <div className="mb-6 rounded-lg border bg-card p-4">
          <h2 className="mb-3 text-base font-semibold">
            {draft.id ? "Edit rule" : "New rule"}
          </h2>
          <RuleEditor draft={draft} setDraft={setDraft} tagNames={tagNames} />
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setDraft(null)}>
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button
              onClick={saveDraft}
              disabled={create.isPending || update.isPending}
            >
              <Check className="mr-2 h-4 w-4" />
              Save
            </Button>
          </div>
        </div>
      ) : null}

      {rules.isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
          No automation rules yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table className="min-w-[760px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">On</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Actions</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.id} className="align-top">
                  <TableCell>
                    <Switch
                      checked={r.enabled}
                      onCheckedChange={(v) => toggleEnabled(r.id, v)}
                      disabled={update.isPending}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {describeTrigger({
                      triggerType: r.triggerType as TriggerType,
                      triggerValue: r.triggerValue,
                    })}
                  </TableCell>
                  <TableCell>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      {((r.actions as RuleAction[]) ?? []).map((a, i) => (
                        <li key={i}>• {describeAction(a)}</li>
                      ))}
                    </ul>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-muted-foreground"
                        onClick={() => startEdit(r)}
                        disabled={!!draft}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            disabled={remove.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove rule?</AlertDialogTitle>
                            <AlertDialogDescription>
                              <span className="font-medium">{r.name}</span> will no
                              longer fire on its trigger. Existing opportunities are
                              untouched.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              onClick={() => remove.mutate({ id: r.id })}
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function RuleEditor({
  draft,
  setDraft,
  tagNames,
}: {
  draft: DraftRule;
  setDraft: (next: DraftRule) => void;
  tagNames: string[];
}) {
  const setTriggerType = (t: TriggerType) => {
    setDraft({
      ...draft,
      triggerType: t,
      triggerValue:
        t === "status_change" ? "approaching" : tagNames[0] ?? "",
    });
  };

  const addAction = (type: ActionType) => {
    const next: RuleAction =
      type === "set_status"
        ? { type: "set_status", status: "approaching" }
        : type === "offset_next_action_days"
          ? { type: "offset_next_action_days", days: 1 }
          : type === "generate_brief"
            ? { type: "generate_brief" }
            : type === "generate_opportunity_picture"
              ? { type: "generate_opportunity_picture" }
              : type === "generate_product_services_note"
                ? { type: "generate_product_services_note" }
                : { type: "add_tag", tagName: tagNames[0] ?? "" };
    setDraft({ ...draft, actions: [...draft.actions, next] });
  };

  const updateAction = (idx: number, next: RuleAction) => {
    const arr = [...draft.actions];
    arr[idx] = next;
    setDraft({ ...draft, actions: arr });
  };

  const removeAction = (idx: number) => {
    const arr = [...draft.actions];
    arr.splice(idx, 1);
    setDraft({ ...draft, actions: arr });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="md:col-span-2">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Name
          </label>
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Quote pending → Approaching"
            maxLength={120}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Order
          </label>
          <Input
            type="number"
            value={draft.sortOrder}
            onChange={(e) =>
              setDraft({ ...draft, sortOrder: Number.parseInt(e.target.value || "0", 10) })
            }
          />
        </div>
      </div>

      <div className="rounded-md border bg-muted/30 p-3">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          When
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <Select value={draft.triggerType} onValueChange={(v) => setTriggerType(v as TriggerType)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="status_change">Status changes to…</SelectItem>
              <SelectItem value="tag_added">Tag is added…</SelectItem>
            </SelectContent>
          </Select>
          {draft.triggerType === "status_change" ? (
            <Select
              value={draft.triggerValue}
              onValueChange={(v) => setDraft({ ...draft, triggerValue: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <TagPickerOrInput
              value={draft.triggerValue}
              onChange={(v) => setDraft({ ...draft, triggerValue: v })}
              tagNames={tagNames}
            />
          )}
        </div>
      </div>

      <div className="rounded-md border bg-muted/30 p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Then
          </span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => addAction("set_status")}>
              + Status
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => addAction("offset_next_action_days")}
            >
              + Next action
            </Button>
            <Button size="sm" variant="outline" onClick={() => addAction("add_tag")}>
              + Tag
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => addAction("generate_brief")}
            >
              + Brief
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => addAction("generate_opportunity_picture")}
            >
              + Picture
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => addAction("generate_product_services_note")}
            >
              + P&S note
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          {draft.actions.length === 0 ? (
            <div className="rounded border border-dashed py-6 text-center text-sm text-muted-foreground">
              No actions yet.
            </div>
          ) : (
            draft.actions.map((action, idx) => (
              <ActionRow
                key={idx}
                action={action}
                tagNames={tagNames}
                onChange={(next) => updateAction(idx, next)}
                onRemove={() => removeAction(idx)}
              />
            ))
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          checked={draft.enabled}
          onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
        />
        <span className="text-sm">Enabled</span>
      </div>
    </div>
  );
}

function ActionRow({
  action,
  tagNames,
  onChange,
  onRemove,
}: {
  action: RuleAction;
  tagNames: string[];
  onChange: (next: RuleAction) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-background px-2 py-2">
      <Select
        value={action.type}
        onValueChange={(v) => {
          const t = v as ActionType;
          if (t === "set_status") onChange({ type: "set_status", status: "approaching" });
          else if (t === "offset_next_action_days")
            onChange({ type: "offset_next_action_days", days: 1 });
          else if (t === "generate_brief") onChange({ type: "generate_brief" });
          else if (t === "generate_opportunity_picture")
            onChange({ type: "generate_opportunity_picture" });
          else if (t === "generate_product_services_note")
            onChange({ type: "generate_product_services_note" });
          else onChange({ type: "add_tag", tagName: tagNames[0] ?? "" });
        }}
      >
        <SelectTrigger className="w-[200px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="set_status">Set status</SelectItem>
          <SelectItem value="offset_next_action_days">Set next action date</SelectItem>
          <SelectItem value="add_tag">Add tag</SelectItem>
          <SelectItem value="generate_brief">Generate brief (AI)</SelectItem>
          <SelectItem value="generate_opportunity_picture">
            Generate opportunity picture (AI)
          </SelectItem>
          <SelectItem value="generate_product_services_note">
            Generate Product &amp; services note (AI)
          </SelectItem>
        </SelectContent>
      </Select>

      {action.type === "set_status" ? (
        <Select
          value={action.status}
          onValueChange={(v) => onChange({ type: "set_status", status: v as Status })}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : action.type === "offset_next_action_days" ? (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">today +</span>
          <Input
            type="number"
            value={action.days}
            onChange={(e) =>
              onChange({
                type: "offset_next_action_days",
                days: Number.parseInt(e.target.value || "0", 10),
              })
            }
            className="w-20"
          />
          <span className="text-sm text-muted-foreground">business days</span>
        </div>
      ) : action.type === "add_tag" ? (
        <TagPickerOrInput
          value={action.tagName}
          onChange={(v) => onChange({ type: "add_tag", tagName: v })}
          tagNames={tagNames}
        />
      ) : (
        <span className="text-sm text-muted-foreground">
          No options — runs the AI generation on the opportunity.
        </span>
      )}

      <div className="ml-auto">
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function TagPickerOrInput({
  value,
  onChange,
  tagNames,
}: {
  value: string;
  onChange: (v: string) => void;
  tagNames: string[];
}) {
  if (tagNames.length === 0) {
    return (
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Tag name"
        className="w-[220px]"
      />
    );
  }
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[220px]">
        <SelectValue placeholder="Pick a tag" />
      </SelectTrigger>
      <SelectContent>
        {tagNames.map((n) => (
          <SelectItem key={n} value={n}>
            {n}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
